import type {TreeOption} from 'naive-ui';
import {
    buildFeatureTreeModel,
    buildPartFeatureDocument,
    buildResultBodyDisplayNameMap,
    DefaultDatumPlaneKind,
    resolveModelBodyIdentity,
    ViewMode,
} from '@puppycad/cad-core';
import {
    type BuildFeatureTreeProjectionOptions,
    type FeatureTreeDatumPlaneSummary,
    type FeatureTreeProjectionResult,
    type FeatureTreeNodeMeta,
    FeatureTreeDataSource,
    FeatureTreeNodeKind,
    DEFAULT_PLANE_ORDER,
} from './feature-tree-types';
import {
    createFeatureManagerAnnotationsNode,
    createFeatureManagerBodyFolderNode,
    createFeatureManagerHistoryNode,
    createFeatureManagerMaterialNode,
} from '../../core/feature-tree/feature-manager-system-nodes';

/**
 * 文件定位：
 * 1. 当前文件位于 `web-client/hooks/feature-tree` 目录；
 * 2. 负责把 cad-core 提供的设计树投影结果适配成 web-client 所需树节点；
 * 3. 该文件只处理节点投影，不负责任何 UI 动作或状态写入。
 *
 * 文件职责：
 * 1. 基于 cad-core 的 `buildPartFeatureDocument/buildFeatureTreeModel` 构建设计树；
 * 2. 统一生成节点元数据映射，避免这些投影细节散落在 store 中；
 * 3. 为后续扩展新特征类型保留单一树投影适配入口。
 */

/**
 * 构建设计树投影。
 * @param options 投影输入。
 * @returns 树节点数据与节点元数据映射。
 */
export interface DatumReferenceTreeProjectionResult {
    /** 顶部固定基准区：默认三基准面与原点。 */
    systemDatumNodes: TreeOption[];
    /** 基准引用节点元数据映射。 */
    featureTreeNodeMap: Map<string, FeatureTreeNodeMeta>;
}

/**
 * 设计树时间线排序条目。
 */
export interface FeatureTreeTimelineSortEntry {
    /** 树节点。 */
    node: TreeOption;
    /** 创建时间戳（毫秒）。 */
    createdAt: number;
    /** 同时间戳下的稳定次序。 */
    tieIndex: number;
}

/**
 * 构建 Worker 命令创建时间索引。
 *
 * 设计说明：
 * 1. `CadCommand.timestamp` 表示特征首次创建时间，编辑特征时在 `FeatureEditOrchestrator` 中保留；
 * 2. 设计树时间线与实体文件夹排序都依赖该字段，禁止把“最后编辑时间”写回 `timestamp`。
 *
 * @param history Worker 命令历史。
 * @returns 命令 ID 到创建时间戳的映射。
 */
function buildCommandCreatedAtMap(history: readonly { id?: unknown; timestamp?: unknown }[]): Map<string, number> {
    const createdAtByCommandId = new Map<string, number>();
    history.forEach((command, index) => {
        if (typeof command.id !== 'string') {
            return;
        }
        const timestamp = Number(command.timestamp);
        createdAtByCommandId.set(
            command.id,
            Number.isFinite(timestamp) ? timestamp : index,
        );
    });
    return createdAtByCommandId;
}

/**
 * 构建草图创建时间索引。
 *
 * @param sceneSketches 场景草图摘要。
 * @param commandCreatedAtById 命令创建时间索引，用于旧数据兜底锚定。
 * @returns 草图 ID 到创建时间戳的映射。
 */
function buildSketchCreatedAtMap(
    sceneSketches: readonly { id: string; order: number; createdAt?: number }[],
    commandCreatedAtById: Map<string, number>,
): Map<string, number> {
    const createdAtBySketchId = new Map<string, number>();
    const knownTimestamps = [
        ...Array.from(commandCreatedAtById.values()),
        ...sceneSketches
            .map((sketch) => sketch.createdAt)
            .filter((value): value is number => Number.isFinite(value)),
    ];
    const minKnownTimestamp = knownTimestamps.length > 0
        ? Math.min(...knownTimestamps)
        : Number.POSITIVE_INFINITY;
    const legacyAnchor = Number.isFinite(minKnownTimestamp)
        ? minKnownTimestamp - sceneSketches.length * 1000
        : Date.now();

    sceneSketches.forEach((sketch) => {
        const createdAt = Number.isFinite(sketch.createdAt)
            ? Number(sketch.createdAt)
            : legacyAnchor + sketch.order * 1000;
        createdAtBySketchId.set(sketch.id, createdAt);
    });
    return createdAtBySketchId;
}

/**
 * 从对象 metadata 中读取可用于树节点归属判断的字符串字段。
 *
 * @param metadata Worker 对象携带的可序列化元数据。
 * @param key 需要读取的字段名。
 * @returns 去除首尾空白后的字符串；字段不存在或为空时返回 `undefined`。
 */
function resolveMetadataString(
    metadata: Record<string, unknown> | undefined,
    key: string,
): string | undefined {
    const value = metadata?.[key];
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

//------ 默认基准面稳定身份 开始------
/**
 * 默认基准面投影顺序项。
 */
interface DefaultDatumPlaneOrderEntry {
    /** 默认基准面稳定身份。 */
    kind: DefaultDatumPlaneKind;
    /** 旧数据或占位节点使用的默认显示名。 */
    fallbackLabel: string;
}

/**
 * 默认三基准面固定投影顺序。
 */
const DEFAULT_DATUM_PLANE_ORDER: readonly DefaultDatumPlaneOrderEntry[] = [
    {
        kind: DefaultDatumPlaneKind.Front,
        fallbackLabel: DEFAULT_PLANE_ORDER[0],
    },
    {
        kind: DefaultDatumPlaneKind.Top,
        fallbackLabel: DEFAULT_PLANE_ORDER[1],
    },
    {
        kind: DefaultDatumPlaneKind.Right,
        fallbackLabel: DEFAULT_PLANE_ORDER[2],
    },
];

/**
 * 按旧显示名兜底解析默认基准面身份。
 *
 * 设计说明：
 * 1. 真实运行时已经由 cad-core 提供 `defaultPlaneKind`；
 * 2. 该兜底只服务旧测试数据或旧摘要对象，不在存在稳定身份时参与真实匹配；
 * 3. 这样可避免用户创建的同名基准面误占默认槽位。
 *
 * @param name 基准面显示名。
 * @returns 默认基准面身份；无法识别时返回 `null`。
 */
function resolveLegacyDefaultDatumPlaneKindByName(name: string): DefaultDatumPlaneKind | null {
    const matchedEntry = DEFAULT_DATUM_PLANE_ORDER.find((entry) => entry.fallbackLabel === name);
    return matchedEntry?.kind ?? null;
}

/**
 * 判断基准面摘要是否携带稳定默认身份。
 *
 * @param plane 基准面摘要。
 * @returns 是否携带稳定默认身份。
 */
function hasStableDefaultDatumPlaneKind(plane: FeatureTreeDatumPlaneSummary): boolean {
    return plane.defaultPlaneKind !== undefined && plane.defaultPlaneKind !== null;
}

/**
 * 解析基准面默认身份。
 *
 * @param plane 基准面摘要。
 * @param allowLegacyNameFallback 是否允许按旧名称兜底。
 * @returns 默认身份；不是默认基准面时返回 `null`。
 */
function resolveDatumPlaneDefaultKind(
    plane: FeatureTreeDatumPlaneSummary,
    allowLegacyNameFallback: boolean,
): DefaultDatumPlaneKind | null {
    if (hasStableDefaultDatumPlaneKind(plane)) {
        return plane.defaultPlaneKind ?? null;
    }
    return allowLegacyNameFallback ? resolveLegacyDefaultDatumPlaneKindByName(plane.name) : null;
}

/**
 * 构建基准面树节点并写入节点元数据。
 *
 * @param plane 基准面摘要。
 * @param featureTreeNodeMap 节点元数据映射。
 * @returns 基准面树节点。
 */
function createDatumPlaneTreeNode(
    plane: FeatureTreeDatumPlaneSummary,
    featureTreeNodeMap: Map<string, FeatureTreeNodeMeta>,
): TreeOption {
    const key = `datum-plane-${plane.id}`;
    featureTreeNodeMap.set(key, {
        kind: FeatureTreeNodeKind.DatumPlane,
        planeId: plane.id,
        //------ 基准面编辑特征 开始------
        canEditReferencePlane: plane.canEditReferencePlane === true,
        //------ 基准面编辑特征 结束------
    });
    return {
        key,
        label: plane.name,
        type: ViewMode.DATUM_PLANE,
        iconType: 'plane',
        statusHidden: !plane.isVisible(),
        isLeaf: true,
    };
}
//------ 默认基准面稳定身份 结束------

/**
 * 构建基准点/轴/面的设计树投影。
 *
 * 设计说明：
 * 1. 基准对象同时服务于原生零件树与 GLB replaceTree 模式，必须抽成单一纯函数避免两边各自拼装；
 * 2. 该函数只关心正式 datum 对象，不处理特征、草图、实体等其它节点；
 * 3. 默认三基准面与原点进入顶部固定基准区；
 * 4. 用户基准面/轴/点改由 cad-core FeatureManager 退回序列权威投影，这里只注册元数据。
 *
 * @param input 基准引用摘要输入。
 * @returns 基准引用节点与元数据映射。
 */
export function buildDatumReferenceTreeProjection(
    input: Pick<BuildFeatureTreeProjectionOptions, 'datumPlanes' | 'datumAxes' | 'datumPoints'>,
): DatumReferenceTreeProjectionResult {
    const {datumPlanes, datumAxes, datumPoints} = input;
    const featureTreeNodeMap = new Map<string, FeatureTreeNodeMeta>();

    /**
     * 基准面不属于 Worker 历史对象，因此单独在宿主侧组织显示顺序与占位节点。
     */
    const defaultDatumPlaneNodes: TreeOption[] = [];
    const hasStableDefaultPlanes = datumPlanes.some((plane) => hasStableDefaultDatumPlaneKind(plane));
    const defaultPlaneByKind = new Map<DefaultDatumPlaneKind, typeof datumPlanes[number]>();
    const extraDatumPlanes: typeof datumPlanes = [];
    datumPlanes.forEach((plane) => {
        const defaultKind = resolveDatumPlaneDefaultKind(plane, !hasStableDefaultPlanes);
        if (defaultKind && !defaultPlaneByKind.has(defaultKind)) {
            defaultPlaneByKind.set(defaultKind, plane);
            return;
        }
        extraDatumPlanes.push(plane);
    });
    DEFAULT_DATUM_PLANE_ORDER.forEach((entry, index) => {
        const plane = defaultPlaneByKind.get(entry.kind);
        if (!plane) {
            const key = `datum-plane-missing-${index}`;
            defaultDatumPlaneNodes.push({
                key,
                label: entry.fallbackLabel,
                type: 'datum-plane-placeholder',
                iconType: 'plane',
                statusSuppressed: true,
                isLeaf: true,
            });
            featureTreeNodeMap.set(key, {kind: FeatureTreeNodeKind.Group});
            return;
        }
        defaultDatumPlaneNodes.push(createDatumPlaneTreeNode(plane, featureTreeNodeMap));
    });

    /**
     * 用户基准面的显示节点由 cad-core 退回时间线渲染；
     * 这里只预注册元数据，供拖拽落点与右键菜单消费。
     */
    extraDatumPlanes.forEach((plane) => {
        createDatumPlaneTreeNode(plane, featureTreeNodeMap);
    });

    /**
     * 用户基准轴同样只注册元数据，不在这里塞入假混排时间线。
     */
    datumAxes.forEach((axis) => {
        const key = `datum-axis-${axis.id}`;
        featureTreeNodeMap.set(key, {
            kind: FeatureTreeNodeKind.DatumAxis,
            axisId: axis.id,
            //------ 基准轴编辑特征 开始------
            canEditReferenceAxis: axis.canEditReferenceAxis === true,
            //------ 基准轴编辑特征 结束------
        });
    });

    /**
     * 非原点用户基准点只注册元数据；显示节点由 cad-core 退回序列权威投影。
     */
    datumPoints
        .filter((point) => !point.isOrigin)
        .forEach((point) => {
            const key = `datum-point-${point.id}`;
            featureTreeNodeMap.set(key, {
                kind: FeatureTreeNodeKind.DatumPoint,
                pointId: point.id,
                //------ 基准点编辑特征 开始------
                canEditReferencePoint: point.canEditReferencePoint === true,
                //------ 基准点编辑特征 结束------
            });
        });

    //------ 原点基准点投影 开始------
    /**
     * 原点是系统内置的默认基准点，为其单独维护元数据。
     */
    const originPoint = datumPoints.find((point) => point.isOrigin);
    const originNodeMeta = originPoint
        ? { kind: FeatureTreeNodeKind.DatumPoint, pointId: originPoint.id }
        : { kind: FeatureTreeNodeKind.Object };
    //------ 原点基准点投影 结束------

    featureTreeNodeMap.set('origin-node', originNodeMeta);

    return {
        systemDatumNodes: [
            ...defaultDatumPlaneNodes,
            {
                key: 'origin-node',
                label: '原点',
                type: 'origin',
                iconType: 'origin',
                isLeaf: true,
            },
        ],
        featureTreeNodeMap,
    };
}

/**
 * 列出应进入 FeatureManager 退回序列的用户基准面。
 *
 * @param datumPlanes 全部基准面摘要。
 * @returns 用户创建基准面；默认三基准面排除。
 */
function listUserCreatedDatumPlanesForFeatureManager(
    datumPlanes: readonly FeatureTreeDatumPlaneSummary[],
): FeatureTreeDatumPlaneSummary[] {
    const hasStableDefaultPlanes = datumPlanes.some((plane) => hasStableDefaultDatumPlaneKind(plane));
    const claimedDefaultKinds = new Set<DefaultDatumPlaneKind>();
    const userCreatedPlanes: FeatureTreeDatumPlaneSummary[] = [];
    datumPlanes.forEach((plane) => {
        const defaultKind = resolveDatumPlaneDefaultKind(plane, !hasStableDefaultPlanes);
        if (defaultKind && !claimedDefaultKinds.has(defaultKind)) {
            claimedDefaultKinds.add(defaultKind);
            return;
        }
        userCreatedPlanes.push(plane);
    });
    return userCreatedPlanes;
}

/**
 * 构建设计树投影。
 * @param options 投影输入。
 * @returns 树节点数据与节点元数据映射。
 */
/**
 * 项目模型加载迁移边界：
 * OpenProject、一级子节点版本切换、插入零部件、项目整体切换完成入口迁移后，
 * 必须统一使用 `project-model-feature-tree-adapter` 消费 cad-core 项目模型设计树投影。
 * 当前入口只保留给非项目模型导入场景，以及尚未迁移的原生零件/GLB 投影链路。
 */
export function buildFeatureTreeProjection(
    options: BuildFeatureTreeProjectionOptions,
): FeatureTreeProjectionResult {
    const {
        objects,
        history,
        datumPlanes,
        datumAxes,
        datumPoints,
        sceneSketches,
        hiddenObjectIds,
        suppressedSketchIds,
        objectNameOverrides,
        partLabel,
        rollbackState,
    } = options;

    const datumProjection = buildDatumReferenceTreeProjection({
        datumPlanes,
        datumAxes,
        datumPoints,
    });
    const featureTreeNodeMap = new Map<string, FeatureTreeNodeMeta>(datumProjection.featureTreeNodeMap);
    const treeNodes: TreeOption[] = [];
    const sketchNodeKey = (id: string): string => `sketch-${id}`;
    const commandCreatedAtById = buildCommandCreatedAtMap(history);
    const sketchCreatedAtById = buildSketchCreatedAtMap(sceneSketches, commandCreatedAtById);
    const userCreatedDatumPlanes = listUserCreatedDatumPlanesForFeatureManager(datumPlanes);

    /**
     * 特征、草图、用户基准面/轴/点完全复用 cad-core 的 FeatureManager 投影。
     * web-client 只做 UI 适配，不再本地拼装退回时间线顺序。
     */
    const document = buildPartFeatureDocument({
        history,
        objects,
        sceneSketches: sceneSketches.map((sketch) => ({
            ...sketch,
            order: sketchCreatedAtById.get(sketch.id) ?? sketch.order,
        })),
        sceneDatumPlanes: userCreatedDatumPlanes.map((plane, index) => ({
            id: String(plane.id),
            name: plane.name,
            order: Number.isFinite(plane.createdAt) ? Number(plane.createdAt) : index,
            hidden: !plane.isVisible(),
        })),
        sceneDatumAxes: datumAxes.map((axis, index) => ({
            id: String(axis.id),
            name: axis.name,
            order: Number.isFinite(axis.createdAt) ? Number(axis.createdAt) : index,
            hidden: !axis.isVisible(),
        })),
        sceneDatumPoints: datumPoints
            .filter((point) => !point.isOrigin)
            .map((point, index) => ({
                id: String(point.id),
                name: point.name,
                order: Number.isFinite(point.createdAt) ? Number(point.createdAt) : index,
                hidden: !point.isVisible(),
            })),
        hiddenObjectIds,
        suppressedSketchIds,
        objectNameOverrides,
        rollbackState,
    });
    const renderModel = buildFeatureTreeModel(document);
    const featureById = new Map(document.features.map((feature) => [feature.id, feature]));
    const bodyDisplayNameMap = buildResultBodyDisplayNameMap({
        objects,
        features: document.features,
        objectNameOverrides,
    });
    renderModel.nodes.forEach((node) => {
        if (node.nodeType === 'rollback-bar') {
            treeNodes.push({
                key: node.key,
                label: node.label,
                type: 'rollback-bar',
                iconType: node.iconType,
                isLeaf: true,
                rollbackCursorIndex: node.rollbackCursorIndex,
                featureManagerIndex: node.featureManagerIndex,
                totalTimelineItemCount: node.totalTimelineItemCount,
                statusRolledBack: node.rolledBack === true,
            });
            featureTreeNodeMap.set(node.key, {
                kind: FeatureTreeNodeKind.RollbackBar,
                rollbackCursorIndex: node.rollbackCursorIndex,
                featureManagerIndex: node.featureManagerIndex,
                totalTimelineItemCount: node.totalTimelineItemCount,
            });
            return;
        }

        if (node.nodeType === 'feature') {
            const feature = node.refObjectId ? featureById.get(node.refObjectId) : undefined;
            /**
             * 吸收草图后特征在退回时间线中锚定草图创建序；
             * createdAt 供 UI/调试阅读，实际节点次序由 cad-core featureManagerIndex 决定。
             */
            const featureCreatedAt = feature?.sourceSketchId
                ? (sketchCreatedAtById.get(feature.sourceSketchId)
                    ?? (node.refObjectId ? commandCreatedAtById.get(node.refObjectId) : undefined))
                : (node.refObjectId ? commandCreatedAtById.get(node.refObjectId) : undefined);
            treeNodes.push({
                key: node.key,
                label: node.label,
                type: 'feature',
                iconType: node.iconType,
                isLeaf: !node.children || node.children.length === 0,
                refObjectId: node.refObjectId,
                statusHidden: node.hidden === true,
                statusRolledBack: node.rolledBack === true,
                createdAt: featureCreatedAt,
                children: node.children?.map((child) => ({
                    key: child.key,
                    label: child.label,
                    type: 'sketch',
                    iconType: child.iconType,
                    isLeaf: true,
                    refObjectId: child.refObjectId,
                    statusHidden: child.hidden === true,
                    statusError: child.referenceWarning?.status === 'error',
                    statusWarning: child.referenceWarning?.status === 'warning',
                    warningMessage: child.referenceWarning?.diagnostics
                        .map((diagnostic) => diagnostic.message)
                        .join('\n'),
                    statusRolledBack: child.rolledBack === true,
                })),
            });
            featureTreeNodeMap.set(node.key, {
                kind: FeatureTreeNodeKind.Object,
                source: FeatureTreeDataSource.State,
                objectId: node.refObjectId,
                objectType: 'feature',
                featureKind: feature?.kind,
                sourceSketchId: feature?.sourceSketchId,
                canEditFeature: feature?.editCapabilities.canEditFeature ?? false,
                canEditSketch: feature?.editCapabilities.canEditSketch ?? false,
                featureCommandIndex: node.featureCommandIndex,
                featureManagerIndex: node.featureManagerIndex,
                rolledBack: node.rolledBack === true,
                resultObjectIds: Array.isArray(node.bodyRefs) ? [...node.bodyRefs] : undefined,
            });
            node.children?.forEach((child) => {
                featureTreeNodeMap.set(child.key, {
                    kind: FeatureTreeNodeKind.Object,
                    source: FeatureTreeDataSource.Scene,
                    objectId: child.refObjectId,
                    objectType: 'sketch',
                    nodeRole: 'sketchRef',
                    rolledBack: child.rolledBack === true,
                });
            });
            return;
        }

        if (node.nodeType === 'datum-plane') {
            const planeId = Number(node.refObjectId);
            const existingMeta = featureTreeNodeMap.get(node.key);
            const plane = document.datumPlanes.find((entry) => entry.id === node.refObjectId);
            treeNodes.push({
                key: node.key,
                label: node.label,
                type: ViewMode.DATUM_PLANE,
                iconType: 'plane',
                isLeaf: true,
                refObjectId: node.refObjectId,
                statusHidden: node.hidden === true,
                statusRolledBack: node.rolledBack === true,
                featureManagerIndex: node.featureManagerIndex,
                createdAt: plane?.order,
            });
            featureTreeNodeMap.set(node.key, {
                kind: FeatureTreeNodeKind.DatumPlane,
                planeId: Number.isFinite(planeId) ? planeId : existingMeta?.planeId,
                canEditReferencePlane: existingMeta?.canEditReferencePlane === true,
                featureManagerIndex: node.featureManagerIndex,
                rolledBack: node.rolledBack === true,
            });
            return;
        }

        if (node.nodeType === 'datum-axis') {
            const axisId = Number(node.refObjectId);
            const existingMeta = featureTreeNodeMap.get(node.key);
            const axis = document.datumAxes.find((entry) => entry.id === node.refObjectId);
            treeNodes.push({
                key: node.key,
                label: node.label,
                type: 'datum-axis',
                iconType: 'line',
                isLeaf: true,
                refObjectId: node.refObjectId,
                statusHidden: node.hidden === true,
                statusRolledBack: node.rolledBack === true,
                featureManagerIndex: node.featureManagerIndex,
                createdAt: axis?.order,
            });
            featureTreeNodeMap.set(node.key, {
                kind: FeatureTreeNodeKind.DatumAxis,
                axisId: Number.isFinite(axisId) ? axisId : existingMeta?.axisId,
                canEditReferenceAxis: existingMeta?.canEditReferenceAxis === true,
                featureManagerIndex: node.featureManagerIndex,
                rolledBack: node.rolledBack === true,
            });
            return;
        }

        if (node.nodeType === 'datum-point') {
            const pointId = Number(node.refObjectId);
            const existingMeta = featureTreeNodeMap.get(node.key);
            const point = document.datumPoints.find((entry) => entry.id === node.refObjectId);
            treeNodes.push({
                key: node.key,
                label: node.label,
                type: 'datum-point',
                iconType: 'point',
                isLeaf: true,
                refObjectId: node.refObjectId,
                statusHidden: node.hidden === true,
                statusRolledBack: node.rolledBack === true,
                featureManagerIndex: node.featureManagerIndex,
                createdAt: point?.order,
            });
            featureTreeNodeMap.set(node.key, {
                kind: FeatureTreeNodeKind.DatumPoint,
                pointId: Number.isFinite(pointId) ? pointId : existingMeta?.pointId,
                canEditReferencePoint: existingMeta?.canEditReferencePoint === true,
                featureManagerIndex: node.featureManagerIndex,
                rolledBack: node.rolledBack === true,
            });
            return;
        }

        const sketch = document.sketches.find((entry) => entry.id === node.refObjectId);
        const key = sketchNodeKey(node.refObjectId ?? node.key);
        treeNodes.push({
            key,
            label: node.label,
            type: 'sketch',
            iconType: node.iconType,
            isLeaf: true,
            refObjectId: node.refObjectId,
            statusHidden: node.hidden === true || sketch?.hidden === true,
            statusSuppressed: sketch?.suppressed ?? false,
            statusError: sketch?.referenceWarning?.status === 'error',
            statusWarning: sketch?.referenceWarning?.status === 'warning',
            warningMessage: sketch?.referenceWarning?.diagnostics
                .map((diagnostic) => diagnostic.message)
                .join('\n'),
            statusRolledBack: node.rolledBack === true,
            featureManagerIndex: node.featureManagerIndex,
            createdAt: node.refObjectId
                ? sketchCreatedAtById.get(node.refObjectId)
                : undefined,
        });
        featureTreeNodeMap.set(key, {
            kind: FeatureTreeNodeKind.Object,
            source: FeatureTreeDataSource.Scene,
            objectId: node.refObjectId,
            objectType: 'sketch',
            featureManagerIndex: node.featureManagerIndex,
            rolledBack: node.rolledBack === true,
        });
    });

    const featureTimelineNodes = treeNodes;

    /**
     * 实体文件夹仍由宿主侧投影。
     * 这是设计树视图结构的一部分，但实体命名语义已经收口到 cad-core，
     * web-client 这里只负责消费统一的结果体显示名映射。
     */
    const bodyNodeEntries: FeatureTreeTimelineSortEntry[] = objects
        .filter((object) => object.type === 'solid')
        .map((object, index) => {
            const key = `body-${object.id}`;
            const createdByFeatureId = resolveMetadataString(object.metadata, 'featureId');
            const bodyBindingId = resolveMetadataString(object.metadata, 'bodyBindingId');
            const modelBodyIdentity = resolveModelBodyIdentity(object);
            const label = bodyDisplayNameMap.get(object.id) ?? `实体${index + 1}`;
            const createdAt = createdByFeatureId
                ? (commandCreatedAtById.get(createdByFeatureId) ?? index)
                : index;
            featureTreeNodeMap.set(key, {
                kind: FeatureTreeNodeKind.Object,
                source: FeatureTreeDataSource.State,
                objectId: object.id,
                objectType: 'body',
                modelBodyId: modelBodyIdentity?.modelBodyId,
                modelBodyRevisionId: modelBodyIdentity?.modelBodyRevisionId,
                createdByFeatureId,
                bodyBindingId,
            });
            return {
                node: {
                    key,
                    label,
                    type: 'body',
                    iconType: 'body',
                    isLeaf: true,
                    refObjectId: object.id,
                    createdAt,
                },
                createdAt,
                tieIndex: index,
            };
        });
    const bodyNodes = bodyNodeEntries
        .slice()
        .sort((left, right) => left.createdAt - right.createdAt || left.tieIndex - right.tieIndex)
        .map((entry) => entry.node);

    const rootChildren: TreeOption[] = [
        createFeatureManagerHistoryNode('history-folder'),
        createFeatureManagerAnnotationsNode('annotations-folder'),
        createFeatureManagerBodyFolderNode({
            key: 'solid-bodies-folder',
            bodyNodes,
            defaultCollapsed: true,
        }),
        createFeatureManagerMaterialNode({
            key: 'material-node',
            type: 'material',
        }),
        ...datumProjection.systemDatumNodes,
        ...featureTimelineNodes,
    ];

    featureTreeNodeMap.set('history-folder', {kind: FeatureTreeNodeKind.Group});
    featureTreeNodeMap.set('solid-bodies-folder', {kind: FeatureTreeNodeKind.Group});
    featureTreeNodeMap.set('annotations-folder', {kind: FeatureTreeNodeKind.Group});
    featureTreeNodeMap.set('material-node', {kind: FeatureTreeNodeKind.Group});
    featureTreeNodeMap.set('part-root', {kind: FeatureTreeNodeKind.Group});

    const resolvedPartLabel = partLabel?.trim() && partLabel.trim().length > 0
        ? partLabel.trim()
        : '零件1<<默认>>';

    return {
        featureTreeData: [{
            key: 'part-root',
            label: resolvedPartLabel,
            type: 'part-root',
            iconType: 'part',
            children: rootChildren,
        }],
        featureTreeNodeMap,
    };
}
